import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ShieldCheck, Pill as PillIcon, FlaskConical, Ban, X, AlertTriangle } from 'lucide-react';
import { toggleCreditPermission, getCreditPermissions } from '../../services/creditLineService';

// Each toggle carries its own identity color (always visible, regardless of
// on/off state) — Medicine = blue, Diagnostic = violet, Both = amber. The
// enabled/disabled state is conveyed separately by the switch, status text,
// and a dim/desaturate treatment when off, so identity and state never fight.
const SCOPE_COLOR = {
  medicine: {
    cardOn: 'bg-gradient-to-br from-sky-50 to-white border-sky-200 hover:shadow-[0_4px_20px_-6px_rgba(14,165,233,0.35)]',
    cardOff: 'bg-slate-50 border-slate-200 hover:border-slate-300',
    iconWrapOn: 'bg-sky-500 text-white shadow-sm shadow-sky-500/30',
    iconWrapOff: 'bg-slate-200 text-slate-400',
    accentText: 'text-sky-600',
    track: '#0ea5e9',
  },
  diagnostic: {
    cardOn: 'bg-gradient-to-br from-violet-50 to-white border-violet-200 hover:shadow-[0_4px_20px_-6px_rgba(139,92,246,0.35)]',
    cardOff: 'bg-slate-50 border-slate-200 hover:border-slate-300',
    iconWrapOn: 'bg-violet-500 text-white shadow-sm shadow-violet-500/30',
    iconWrapOff: 'bg-slate-200 text-slate-400',
    accentText: 'text-violet-600',
    track: '#8b5cf6',
  },
  both: {
    cardOn: 'bg-gradient-to-br from-amber-50 to-white border-amber-200 hover:shadow-[0_4px_20px_-6px_rgba(245,158,11,0.35)]',
    cardOff: 'bg-slate-50 border-slate-200 hover:border-slate-300',
    iconWrapOn: 'bg-amber-500 text-white shadow-sm shadow-amber-500/30',
    iconWrapOff: 'bg-slate-200 text-slate-400',
    accentText: 'text-amber-600',
    track: '#f59e0b',
  },
};

const ModernToggle = ({ scope, checked, onChange, label, icon: Icon, disabled = false }) => {
  const c = SCOPE_COLOR[scope];
  return (
    <motion.button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      whileTap={{ scale: 0.98 }}
      className={`relative flex items-center gap-3 rounded-2xl px-4 py-3.5 border text-left overflow-hidden transition-all duration-300 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? c.cardOn : c.cardOff}`}
    >
      <motion.span
        key={checked ? 'on' : 'off'}
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 450, damping: 20 }}
        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-300 ${checked ? c.iconWrapOn : c.iconWrapOff}`}
      >
        <Icon className="w-4.5 h-4.5" />
      </motion.span>

      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-semibold truncate ${checked ? 'text-gray-900' : 'text-slate-500'}`}>{label}</span>
        <AnimatePresence mode="wait">
          <motion.span
            key={checked ? 'enabled' : 'disabled'}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className={`block text-[11px] font-semibold ${checked ? c.accentText : 'text-slate-400'}`}
          >
            {checked ? 'Enabled' : 'Disabled'}
          </motion.span>
        </AnimatePresence>
      </span>

      <span
        className="relative w-11 h-6 rounded-full shrink-0 transition-colors duration-300"
        style={{ backgroundColor: checked ? c.track : '#cbd5e1' }}
      >
        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.25)] flex items-center justify-center"
          style={{ left: checked ? '22px' : '2px' }}
        />
      </span>
    </motion.button>
  );
};

const ConfirmModal = ({ pending, onCancel, onConfirm, submitting }) => (
  <AnimatePresence>
    {pending && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && !submitting && onCancel()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4"
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
            pending.nextEnabled ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
          }`}>
            {pending.nextEnabled ? <ShieldCheck className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {pending.nextEnabled ? 'Enable' : 'Disable'} {pending.scopeLabel} credit line?
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              This will {pending.nextEnabled ? 'restore' : 'block'} the subscriber's ability to pay via credit line for {pending.scopeLabel.toLowerCase()} orders.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <motion.button
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              whileTap={{ scale: 0.97 }}
              className={`px-4 py-2 text-sm font-semibold rounded-xl text-white transition-colors disabled:opacity-60 ${
                pending.nextEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#0F4C81] hover:bg-[#0C3E6B]'
              }`}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 0.7, ease: 'linear' }}
                    className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full"
                  />
                  Confirming…
                </span>
              ) : 'Confirm'}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Credit line permission toggles — rendered as a row-section INSIDE
// SubscriberInfoCard (not a standalone card), matching its section style
// (e.g. "Identity Documents"). Visibility gate: SuperAdmin only + subscriber
// has an active credit line account.
const CreditPermissionToggles = ({ isSuperAdmin, hasCreditLineAccount, applicationId, currentRole }) => {
  const [permissions, setPermissions] = useState({ medicine: true, diagnostic: true });
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const shouldRender = isSuperAdmin && hasCreditLineAccount;

  useEffect(() => {
    if (!shouldRender || !applicationId) return;
    setLoaded(false);
    getCreditPermissions(currentRole, applicationId)
      .then((res) => {
        if (!res?.metadata?.api_status) throw new Error('Failed to load credit permissions');
        setPermissions({
          medicine: Boolean(res.msg?.med_enable),
          diagnostic: Boolean(res.msg?.diag_enable),
        });
      })
      .catch((err) => setError(err.message || 'Failed to load credit permissions'))
      .finally(() => setLoaded(true));
  }, [shouldRender, applicationId, currentRole]);

  if (!shouldRender) return null;

  const requestToggle = (scope, scopeLabel, nextEnabled) => {
    setError('');
    setPending({ scope, scopeLabel, nextEnabled });
  };

  const confirmToggle = async () => {
    if (!pending) return;
    const { scope, nextEnabled } = pending;
    setSubmitting(true);
    setError('');
    try {
      const res = await toggleCreditPermission(currentRole, applicationId, scope, nextEnabled);
      if (!res?.metadata?.api_status) throw new Error(typeof res?.msg === 'string' ? res.msg : 'Failed to update credit permission');
      setPermissions((prev) => ({
        ...prev,
        ...(scope === 'both' ? { medicine: nextEnabled, diagnostic: nextEnabled } : { [scope]: nextEnabled }),
      }));
      setPending(null);
    } catch (err) {
      setError(err.message || 'Failed to update credit permission');
    } finally {
      setSubmitting(false);
    }
  };

  const bothEnabled = permissions.medicine && permissions.diagnostic;

  return (
    <div className="pt-1 border-t border-slate-100">
      <div className="flex items-center justify-between mb-3 mt-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-sky-500 via-violet-500 to-amber-500 flex items-center justify-center shadow-sm shrink-0">
            <ShieldCheck className="w-3.5 h-3.5 text-white" />
          </span>
          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">Credit Line Permissions</p>
        </div>
        <p className="text-[10px] text-slate-400 font-medium">Pay-via-credit-line, per service</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ModernToggle
          scope="medicine"
          icon={PillIcon}
          checked={permissions.medicine}
          onChange={() => requestToggle('medicine', 'Medicine', !permissions.medicine)}
          label="Medicine"
          disabled={!loaded}
        />
        <ModernToggle
          scope="diagnostic"
          icon={FlaskConical}
          checked={permissions.diagnostic}
          onChange={() => requestToggle('diagnostic', 'Diagnostic', !permissions.diagnostic)}
          label="Diagnostic"
          disabled={!loaded}
        />
        <ModernToggle
          scope="both"
          icon={Ban}
          checked={bothEnabled}
          onChange={() => requestToggle('both', 'Medicine & Diagnostic', !bothEnabled)}
          label="Both"
          disabled={!loaded}
        />
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            className="px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 overflow-hidden"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal pending={pending} onCancel={() => setPending(null)} onConfirm={confirmToggle} submitting={submitting} />
    </div>
  );
};

export default CreditPermissionToggles;
